/**
 * Chart-series builders for the User Metrics overview graphs.
 *
 * The individual getters are also consumed directly by Business Reporting, which
 * is why each takes its own optional filter set rather than relying on the
 * already-filtered data createUserMetricsGraphDataV2 passes in.
 */
import moment from 'moment';
import {
  CalculatedUserMetricsGraphInfo,
  UserMetricsDataForGraphProcessing,
  UserMetricsMasterFilters,
  UserMetricsParsedData
} from '../UserMetricsInterfaces';
import { findCommonElements3 } from '../../fintech-ops-dashboard/FinTechOpsUtils';
import { ALL_APPLICATIONS, FilterDateFormat_MMM_YYYY } from '../../FinTechOpsConstants';
import { aggregateMetricsData, getSortedArrayWithNumber } from 'src/utilities/CommonUtilities';
import {
  USER_METRICS_TOP_5_APPLICATION_BY_MONTH_COLUMN_LIST,
  USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST,
  USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_COLUMN_LIST,
  USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_MEASURE_COLUMN_LIST
} from '../UserMetricsConstants';

export const createUserMetricsGraphDataV2 = (actualUserMetricsParsedData: UserMetricsParsedData[]): CalculatedUserMetricsGraphInfo => {
  const totalAppViewByTeam: UserMetricsDataForGraphProcessing[] = getTotalApplicationViewByTeam(actualUserMetricsParsedData);

  const top5AppViewership: UserMetricsDataForGraphProcessing[] = getTop5ApplicationsByMonth(actualUserMetricsParsedData);

  const uniqueUsersByAppAndMonth: UserMetricsDataForGraphProcessing[] = getTotalUniqueUsersByApplicationAndMonth(actualUserMetricsParsedData);
  const totalViewByApplicationType: UserMetricsDataForGraphProcessing[] = getTotalViewsByApplicationType(actualUserMetricsParsedData);

  return {
    appViewerShipTeamGraph: totalAppViewByTeam,
    top5AppViewershipGraph: top5AppViewership,

    uniqueUsersByAppGraph: uniqueUsersByAppAndMonth,
    totalAppViewsGraph: totalViewByApplicationType
  };
};

// Total Application Views by Team
export function getTotalApplicationViewByTeam(
  actualUserMetricsParsedData: UserMetricsParsedData[],
  userMetricsMasterFilters?: UserMetricsMasterFilters,
  businessReportingMode = false
): UserMetricsDataForGraphProcessing[] {
  const result: UserMetricsDataForGraphProcessing[] = [];

  const excludingAllApplications = actualUserMetricsParsedData.filter((item: UserMetricsParsedData) => item.application !== ALL_APPLICATIONS);

  let filteredData: UserMetricsParsedData[];

  if (businessReportingMode) {
    // Business reporting mode - use pre-filtered data and exclude unmapped teams
    filteredData = excludingAllApplications.filter((item: UserMetricsParsedData) => item.team_name !== 'Unmapped' && item.team_name?.trim() !== '');
  } else {
    // Original user metrics mode - ensure filters exist
    if (!userMetricsMasterFilters) {
      filteredData = excludingAllApplications;
    } else {
      filteredData = excludingAllApplications.filter((item: UserMetricsParsedData) => {
        return (
          findCommonElements3(userMetricsMasterFilters.managers, item.manager_name_hierarchy_concat) &&
          userMetricsMasterFilters.team.includes(item.team_name) &&
          userMetricsMasterFilters.application_type.includes(item.application_type) &&
          userMetricsMasterFilters.application_name.includes(item.application) &&
          userMetricsMasterFilters.month.includes(moment(item.log_month).format(FilterDateFormat_MMM_YYYY)) &&
          userMetricsMasterFilters.user_login.includes(item.user_login)
        );
      });
    }
  }

  const dataMap = new Map<string, number>();

  for (const item of filteredData) {
    const team = item.team_name;
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")

    const key = `${team}_${month}`;
    const count = item.measure_value;

    if (dataMap.has(key)) {
      dataMap.set(key, dataMap.get(key)! + count);
    } else {
      dataMap.set(key, count);
    }
  }

  dataMap.forEach((count, key) => {
    const [team, month] = key.split('_');
    // Keep YYYY-MM format for business reporting, use YYYYMM for user metrics
    const categoryFormat = businessReportingMode ? month : month.replace('-', '');
    result.push({ title: team, category: categoryFormat, count });
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}

// Top 5 Application - Unique Users
export function getTop5ApplicationsByMonth(
  actualUserMetricsParsedData: UserMetricsParsedData[],
  userMetricsMasterFilters?: UserMetricsMasterFilters,
  businessReportingMode = false
): UserMetricsDataForGraphProcessing[] {
  const result: UserMetricsDataForGraphProcessing[] = [];
  const excludingAllApplications = actualUserMetricsParsedData.filter((item: UserMetricsParsedData) => item.application !== ALL_APPLICATIONS);

  let filteredData: UserMetricsParsedData[];

  if (businessReportingMode || !userMetricsMasterFilters) {
    // Business reporting mode or no filters - use pre-filtered data
    filteredData = excludingAllApplications;
  } else {
    // Original user metrics mode
    filteredData = excludingAllApplications.filter((item: UserMetricsParsedData) => {
      return (
        findCommonElements3(userMetricsMasterFilters.managers, item.manager_name_hierarchy_concat) &&
        userMetricsMasterFilters.team.includes(item.team_name) &&
        userMetricsMasterFilters.application_type.includes(item.application_type) &&
        userMetricsMasterFilters.application_name.includes(item.application) &&
        userMetricsMasterFilters.month.includes(moment(item.log_month).format(FilterDateFormat_MMM_YYYY)) &&
        userMetricsMasterFilters.user_login.includes(item.user_login)
      );
    });
  }
  const aggregatedData =
    aggregateMetricsData(
      USER_METRICS_TOP_5_APPLICATION_BY_MONTH_COLUMN_LIST,
      filteredData,
      USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST
    ) || [];
  const dataMap = new Map<string, Map<string, number>>();
  // for (const item of filteredData) {
  for (const item of aggregatedData) {
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")
    const application = item.application;
    const distinctUsers = item.user_login_count_distinct;

    if (dataMap.has(month)) {
      const monthMap = dataMap.get(month)!;
      if (monthMap.has(application)) {
        monthMap.set(application, monthMap.get(application)! + distinctUsers);
      } else {
        monthMap.set(application, distinctUsers);
      }
    } else {
      const monthMap = new Map<string, number>();
      monthMap.set(application, distinctUsers);
      dataMap.set(month, monthMap);
    }
  }
  // Iterate over the map and get the top 5 applications for each month
  dataMap.forEach((monthMap, month) => {
    const sortedApplications = [...monthMap.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

    for (const [application, count] of sortedApplications) {
      result.push({
        title: application,
        category: month,
        count: count
      });
    }
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}

// Total Unique Users by Application Type
// include only All_Apps
export function getTotalUniqueUsersByApplicationAndMonth(actualUserMetricsParsedData: UserMetricsParsedData[]): UserMetricsDataForGraphProcessing[] {
  // ALL_APPLICATIONS aggregate rows are filtered out during data parsing (returnUsefulRawUserMetricsData),
  // so no filtering is needed here. The aggregation by application_type + log_month with countDistinct
  // on user_login will produce correct unique user counts per application type from individual records.

  const result: UserMetricsDataForGraphProcessing[] = [];

  const dataMap = new Map<string, Map<string, number>>();

  const aggregatedData =
    aggregateMetricsData(
      USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_COLUMN_LIST,
      actualUserMetricsParsedData,
      USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_MEASURE_COLUMN_LIST
    ) || [];

  for (const item of aggregatedData) {
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")
    const applicationType = item.application_type;
    const distinctUsers = item.user_login_count_distinct;

    if (dataMap.has(month)) {
      const monthMap = dataMap.get(month)!;
      if (monthMap.has(applicationType)) {
        monthMap.set(applicationType, monthMap.get(applicationType)! + distinctUsers);
      } else {
        monthMap.set(applicationType, distinctUsers);
      }
    } else {
      const monthMap = new Map<string, number>();
      monthMap.set(applicationType, distinctUsers);
      dataMap.set(month, monthMap);
    }
  }

  // Iterate over the map and get the total unique user count for each application type in each month
  dataMap.forEach((monthMap, month) => {
    monthMap.forEach((count, applicationType) => {
      result.push({
        title: applicationType,
        category: month,
        count
      });
    });
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}

// Total Views by Application Type
export function getTotalViewsByApplicationType(actualUserMetricsParsedData: UserMetricsParsedData[]): UserMetricsDataForGraphProcessing[] {
  // ALL_APPLICATIONS aggregate rows are filtered out during data parsing (returnUsefulRawUserMetricsData),
  // so no filtering is needed here. Summing measure_value by application_type + log_month
  // will produce correct total views per application type from individual records.

  const result: UserMetricsDataForGraphProcessing[] = [];

  const dataMap = new Map<string, Map<string, number>>();

  for (const item of actualUserMetricsParsedData) {
    const month = item.log_month.substring(0, 7); // Extract YYYY-MM from log_month (eg: "2023-04-01")
    const applicationType = item.application_type;
    const measureValue = item.measure_value;

    if (dataMap.has(month)) {
      const monthMap = dataMap.get(month)!;
      if (monthMap.has(applicationType)) {
        monthMap.set(applicationType, monthMap.get(applicationType)! + measureValue);
      } else {
        monthMap.set(applicationType, measureValue);
      }
    } else {
      const monthMap = new Map<string, number>();
      monthMap.set(applicationType, measureValue);
      dataMap.set(month, monthMap);
    }
  }

  // Iterate over the map and get the total unique user count for each application type in each month
  dataMap.forEach((monthMap, month) => {
    monthMap.forEach((count, applicationType) => {
      result.push({
        title: applicationType,
        category: month,
        count
      });
    });
  });

  return getSortedArrayWithNumber(result, 'count', 'desc');
}
